/**
 * Client-side image compression.
 *
 * Why this exists:
 *   Production nginx ingress caps request body at ~1 MB. A raw camera JPEG
 *   from a modern phone is 3–7 MB which becomes ~5–10 MB after base64
 *   encoding, causing "Cannot reach the server right now" errors on every
 *   photo-bearing endpoint. This utility shrinks every upload to ≤200 KB
 *   without losing the visual fidelity we actually need (KM digits, slip
 *   text, visitor face).
 *
 * Strategy:
 *   1. Read the raw File via FileReader → dataURL.
 *   2. Decode into an off-screen <canvas>.
 *   3. Downscale so the longest edge ≤ MAX_EDGE_PX (preserves aspect ratio).
 *   4. Re-encode as JPEG at INITIAL_QUALITY; if still > MAX_BYTES, retry
 *      with progressively lower quality (max 4 passes).
 *   5. Return a base64 data URL ready to drop into the existing
 *      `photo_base64` JSON field — call-sites need no other changes.
 *
 * Graceful fallback: if anything throws (very old WebView, decode failure,
 * etc.) we return the raw FileReader result so the user can still submit
 * — just at the original size.
 */

const MAX_EDGE_PX = 1600;      // longest side; KM digits stay crisp
const INITIAL_QUALITY = 0.85;
const MIN_QUALITY = 0.45;
const MAX_BYTES = 400 * 1024;  // v2.6.1 — 400 KB cap, readable quality (user request)

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error || new Error('FileReader failed'));
    r.onloadend = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = dataUrl;
  });
}

function approxBase64Bytes(dataUrl) {
  // dataURL header is ~30 chars; body is base64 → bytes ≈ len * 3/4
  const commaIdx = dataUrl.indexOf(',');
  const b64Len = commaIdx >= 0 ? dataUrl.length - commaIdx - 1 : dataUrl.length;
  return Math.ceil(b64Len * 0.75);
}

/**
 * Compress a File / Blob into a base64 JPEG data URL.
 *
 * @param {File | Blob} file  raw input from <input type="file"> or capture
 * @param {object} [opts]
 * @param {number} [opts.maxEdgePx=1280] longest-side cap in pixels
 * @param {number} [opts.maxBytes=220KB]  final size target
 * @returns {Promise<string>} base64 dataURL (always returns something usable)
 */
export async function compressImage(file, opts = {}) {
  const maxEdge = opts.maxEdgePx || MAX_EDGE_PX;
  const targetBytes = opts.maxBytes || MAX_BYTES;

  if (!file) return '';

  let originalDataUrl = '';
  try {
    originalDataUrl = await fileToDataURL(file);
  } catch {
    return ''; // truly unreadable
  }

  // If already tiny (≤120 KB), don't bother re-encoding — keeps original quality.
  if (approxBase64Bytes(originalDataUrl) <= 120 * 1024) {
    return originalDataUrl;
  }

  try {
    const img = await loadImage(originalDataUrl);
    const longest = Math.max(img.width, img.height);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return originalDataUrl;
    ctx.drawImage(img, 0, 0, w, h);

    let quality = INITIAL_QUALITY;
    let out = canvas.toDataURL('image/jpeg', quality);
    let passes = 0;
    while (approxBase64Bytes(out) > targetBytes && quality > MIN_QUALITY && passes < 4) {
      quality = Math.max(MIN_QUALITY, quality - 0.12);
      out = canvas.toDataURL('image/jpeg', quality);
      passes += 1;
    }
    return out;
  } catch (e) {
    console.warn('compressImage: fallback to original', e);
    return originalDataUrl;
  }
}
